<template>
  <UiComponentWrapper class="page">
    <UiComponentWrapper class="header">
      <UiComponentContainer
        :end="0"
        :start="0"
      >
        <UiComponentWrapper class="left">
          <AtomButton
            type="text"
            :to="
              localePath({
                name: 'blog-category-slug',
                params: {
                  slug: data.page.blogPostCategory[0].slug,
                },
              })
            "
          >
            <UiComponentTag size="s">
              {{
                data.page.blogPostCategory[0].name
              }}
            </UiComponentTag>
          </AtomButton>
          <UiComponentHeading :level="1">
            {{ data.page.title }}
          </UiComponentHeading>
          <UiComponentParagraph v-if="!data.page.headerCta">
            {{
              data.page.subtitle
            }}
          </UiComponentParagraph>
          <div
            class="informations"
            v-html="informations"
          />
          <UiComponentWrapper
            v-if="
              data.page.blogPostAuthor && data.page.blogPostAuthor.avatar && data.page.blogPostAuthor.slug
            "
            class="author"
          >
            <UiComponentImage
              :alt="data.page.blogPostAuthor.name"
              :expected-size="24"
              :height="24"
              :lazyload="false"
              :source="data.page.blogPostAuthor.avatar.url"
              :width="24"
            />
            <AtomButton
              type="text"
              :to="
                localePath({
                  name: 'blog-author-slug',
                  params: { slug: data.page.blogPostAuthor.slug },
                })
              "
            >
              <UiComponentParagraph>
                {{ data.hub.writtenBy }}
                {{ data.page.blogPostAuthor.name }}
              </UiComponentParagraph>
            </AtomButton>
          </UiComponentWrapper>
          <UiComponentWrapper
            v-if="headerCallToActionExists"
            class="header-cta"
          >
            <UiComponentParagraph>{{ data.page.cluster.headerCtaText }}</UiComponentParagraph>
            <AtomButton
              :action="headerCallToAction.event && headerCallToAction.event.name"
              :attributes="headerCallToAction.event && headerCallToAction.event.attributes"
              :to="headerCallToAction.to"
            >
              <UiComponentIcon
                v-if="headerCallToAction.hasIcon"
                type="download"
              />
              {{ headerCallToAction.slot }}
            </AtomButton>
          </UiComponentWrapper>
        </UiComponentWrapper>
        <UiComponentImage
          v-if="heroImage"
          class="right"
          :alt="heroImage.alt"
          :expected-size="620"
          :extra-params="{
            h: 360,
            w: 620,
            fit: 'crop',
          }"
          :height="360"
          :source="heroImage.url"
          :width="620"
        />
      </UiComponentContainer>
    </UiComponentWrapper>
    <div
      class="indicator"
      :style="{ width: `${readingLevelInPercentage}%` }"
    />
    <UiComponentContainer
      :start="0"
      :end="0"
      class="content"
    >
      <UiComponentWrapper class="socials-links">
        <AtomAnchor
          :to="`https://twitter.com/intent/tweet?text=${publicRuntimeConfig.baseUrl}${path}`"
          @click="
            track('Share clicked', {
              category: 'Button',
              label: 'Twitter content',
            })
          "
        >
          <UiComponentIcon type="twitter" />
        </AtomAnchor>
        <AtomAnchor
          :to="`https://www.linkedin.com/sharing/share-offsite/?url=${publicRuntimeConfig.baseUrl}${path}`"
          @click="
            track('Share clicked', {
              category: 'Button',
              label: 'Linkedin content',
            })
          "
        >
          <UiComponentIcon type="linkedin" />
        </AtomAnchor>
        <AtomAnchor
          :to="`http://www.facebook.com/sharer.php?u=${publicRuntimeConfig.baseUrl}${path}`"
          @click="
            track('Share clicked', {
              category: 'Button',
              label: 'Facebook content',
            })
          "
        >
          <UiComponentIcon type="facebook" />
        </AtomAnchor>
      </UiComponentWrapper>
      <UiComponentWrapper class="primary-content">
        <OrganismRichText
          :key="`rich-text-${count}`"
          :content="content.flat()"
          :primary-button="primaryButton"
        />
        <UiComponentWrapper
          v-if="bottomContentOffer"
          class="footer"
        >
          <MoleculeCallToAction
            :pattern-desktop="{
              url: 'https://livestorm.imgix.net/1127/1647965554-pattern-dark.jpg',
            }"
            :pattern-mobile="{
              url: 'https://livestorm.imgix.net/1127/1647966002-pattern-dark-mobile.jpg',
            }"
            :call-to-action="
              data.page.cluster.relatedContentOffer
                ? {
                  event: trackingPlanOfTheBottomContentCard,
                  slot: data.page.cluster.urlForTheCta.includes('app.livestorm.co/#/signup')
                    ? primaryButton.slot
                    : data.page.cluster.bottomContentCardCtaTitle,
                  to: data.page.cluster.urlForTheCta.includes('app.livestorm.co/#/signup')
                    ? primaryButton.to
                    : ctaLink,
                  tracking: ctaLink === '#form-ebook' ? false : true,
                }
                : {
                  event: trackingPlanOfTheBottomContentCard,
                  slot: data.page.cluster.urlForTheCta.includes('app.livestorm.co/#/signup')
                    ? primaryButton.slot
                    : data.page.cluster.bottomContentCardCtaTitle,
                  to: data.page.cluster.urlForTheCta.includes('app.livestorm.co/#/signup')
                    ? primaryButton.to
                    : data.page.cluster.urlForTheCta,
                  tracking: true,
                }
            "
          >
            <template #heading>
              {{
                data.page.cluster.bottomContentCardTitle
              }}
            </template>
            <template #paragraph>
              {{
                data.page.cluster.bottomContentCardText
              }}
            </template>
          </MoleculeCallToAction>
          <OrganismContentCard
            v-if="contentCardCTA"
            as="paragraph"
            :content="{
              ...contentCardCTA,
              event: {
                name: 'Cluster CTA clicked',
                attributes: {
                  ...contentCardCTA.event.attributes,
                  label: 'Inline content card tertiary',
                },
              },
            }"
          />
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper class="table-content-container">
        <MoleculeTableOfContentBlog
          v-if="h2InContent.length > 0"
          :active-index="activeH2Index"
          :content="h2InContent.map(({ textContent }) => textContent)"
          :is-stuck="isStuck"
          @table-of-content:click="goToTitle"
          @select:change="goToTitle"
        >
          {{ data.hub.tableOfContent }}
        </MoleculeTableOfContentBlog>
        <OrganismContentCard
          v-if="displaySidebarContentCard"
          as="paragraph"
          :content="sidebarContentCardData"
        />
      </UiComponentWrapper>
    </UiComponentContainer>
    <UiComponentContainer
      :start="1"
      :end="4"
      class="author"
    >
      <MoleculeBlogAuthor
        v-if="
          data.page.blogPostAuthor && data.page.blogPostAuthor.avatar && data.page.blogPostAuthor.slug
        "
        :author="{
          ...data.page.blogPostAuthor,
          job: data.page.blogPostAuthor.jobTitle,
          text: data.page.blogPostAuthor.shortDescription,
        }"
        in-content
      >
        {{ data.hub.aboutTheAuthor }}
      </MoleculeBlogAuthor>
    </UiComponentContainer>
    <UiComponentWrapper
      v-if="data.page.cluster && data.page.cluster.displayCustomerLogoSection"
      class="reviews"
    >
      <UiComponentContainer>
        <OrganismCompaniesTrust
          :companies="companies"
          :call-to-action="{
            ...primaryButton,
            label: 'Customer section CTA',
          }"
          :hover="data.componentCustomersReviewLogo.hoverText"
        >
          <template #heading>
            {{ data.componentCustomersReviewLogo.text }}
          </template>
          <template #rating>
            {{ data.componentCustomersReviewLogo.reviews }}
          </template>
        </OrganismCompaniesTrust>
      </UiComponentContainer>
    </UiComponentWrapper>
    <UiComponentContainer
      v-if="displayRelatedPillarPage"
      :start="1"
      :end="1"
      class="related-pillar"
    >
      <UiComponentHeading
        as="paragraph"
        :level="4"
      >
        {{
          data.page.cluster.relatedPillarPageTitleOfTheBlock
        }}
      </UiComponentHeading>
      <OrganismContentCard
        as="paragraph"
        :content="relatedPillarPageContent"
        highlighted
      />
    </UiComponentContainer>
    <UiComponentWrapper
      v-if="data.relatedBlogPosts.length > 0"
      class="related-posts"
    >
      <UiComponentContainer
        :start="0"
        :end="0"
      >
        <UiComponentHeading
          as="paragraph"
          :level="3"
        >
          {{
            data.hub.relatedArticles
          }}
        </UiComponentHeading>
        <UiComponentWrapper>
          <MoleculeBlogPost
            v-for="post in data.relatedBlogPosts"
            :key="post.id"
            as="paragraph"
            :minutes-read="data.componentGlobal.minRead"
            :post="post"
          />
        </UiComponentWrapper>
      </UiComponentContainer>
    </UiComponentWrapper>
    <UiComponentContainer
      :start="2"
      :end="2"
      class="newsletter"
    >
      <MoleculeNewsletter
        as="paragraph"
        :icon="data.componentNewsletter.icon"
        @newsletter:subscribe="subscribeToTheNewsletter($event)"
      >
        <template #tag>
          {{ data.componentNewsletter.tag }}
        </template>
        <template #title>
          {{ data.componentNewsletter.title }}
        </template>
      </MoleculeNewsletter>
    </UiComponentContainer>
    <svg
      class="absolute h-0"
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
    >
      <defs>
        <filter id="grey-600">
          <feColorMatrix
            color-interpolation-filters="sRGB"
            type="matrix"
            values="0.3647058824 0 0 0 0
                          0 0.4274509804 0 0 0
                          0 0 0.4745098039 0 0
                          0 0 0 1 0 "
          />
        </filter>
        <filter id="twitter">
          <feColorMatrix
            color-interpolation-filters="sRGB"
            type="matrix"
            values="0.1098039216 0 0 0 0
                          0 0.6117647059 0 0 0
                          0 0 0.937254902 0 0
                          0 0 0 1 0 "
          />
        </filter>
        <filter id="facebook">
          <feColorMatrix
            color-interpolation-filters="sRGB"
            type="matrix"
            values="0.0470588235 0 0 0 0
                          0 0.4 0 0 0
                          0 0 0.7607843137 0 0
                          0 0 0 1 0 "
          />
        </filter>
        <filter id="linkedin">
          <feColorMatrix
            color-interpolation-filters="sRGB"
            type="matrix"
            values="0.1019607843 0 0 0 0
                          0 0.4509803922 0 0 0
                          0 0 0.8901960784 0 0
                          0 0 0 1 0 "
          />
        </filter>
      </defs>
    </svg>
  </UiComponentWrapper>
</template>

<script setup>
// Layout
definePageMeta({
  layout: 'default',
  middleware: [
    'check-if-the-blog-post-exists',
  ],
})

// i18n
defineI18nRoute({
  paths: {
    de: '/blog/[slug]',
    en: '/blog/[slug]',
    es: '/blog/[slug]',
    fr: '/blog/[slug]',
  },
})

// Composables
const { page, track } = useAnalyticsJS()
const { setAvailableLanguages } = useAvailableLanguages()
const { updateListItems } = useBreadcrumb()
const { state } = useConditionalPreviewMode()
const { locale } = useI18n()
const { isLogged } = useIsLoggedIn()
const localePath = useLocalePath()
const { $md } = useMarkdown()
const { $slugify } = useNuxtApp()
const { setResource } = useResource()
const { meta, params, path } = useRoute()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()

// Async Data
const { data } = await useAsyncData(
  `page-blog-[slug]-${params.slug}-${locale.value}`,
  async () => {
    return Promise.all([
      $fetch(`/api/content/pages/blog-index/${locale.value}`, { query: { preview: state.preview } }),
      $fetch(`/api/content/pages/blog-posts/${locale.value}`, { query: { preview: state.preview } }),
    ])
  },
  {
    transform: ([{ allBlogPostCategories: allCategories, allModelOfferTypes: allResourceTypes, componentCustomersReviewLogo, componentGlobal, componentNewsletter, componentPromotion, componentResourceType, home, page: hub }, allBlogPosts]) => {
      const page = allBlogPosts.find(({ slug }) => slug === params.slug)
      const relatedBlogPosts = returnUniqueObjectFromArray(
        [
          ...page.relatedArticles,
          ...allBlogPosts.filter(
            ({ cluster }) => cluster && page.cluster && cluster.id === page.cluster.id,
          ),
          ...allBlogPosts.slice(0, 18),
        ],
        'id',
      )
        .filter(({ id }) => id !== page.id)
        .slice(0, 6)
        .map(
          ({
            id,
            blogPostCategory,
            coverImage,
            coverWithImgix,
            slug,
            title,
          }) => {
            return {
              id,
              coverImage: coverWithImgix || coverImage,
              slug,
              tag: {
                link: blogPostCategory[0].slug,
                name: blogPostCategory[0].name,
              },
              title,
            }
          },
        )

      return {
        allCategories,
        allResourceTypes,
        componentCustomersReviewLogo,
        componentGlobal,
        componentNewsletter,
        componentPromotion,
        componentResourceType,
        home,
        hub,
        page,
        relatedBlogPosts,
      }
    },
  },
)

if (!data.value?.page) {
  throw createError({ statusCode: 404, message: 'Page not found' })
}

// Meta
const { alternativeVersions } = data.value.page
const locales = alternativeVersions.map(({ locale }) => locale)

meta.nuxtI18n = alternativeVersions.reduce(
  (accumator, { locale, value: slug }) => ({
    ...accumator,
    [locale]: { slug },
  }),
  {},
)

const head = useLocaleHead({
  identifierAttribute: 'id',
  addSeoAttributes: true,
})

useHead({
  link: [
    ...head.value.link
      .filter((link) => {
        return link.rel === 'canonical'
          || (link.rel === 'alternate' && link.hreflang === 'x-default')
          || (link.rel === 'alternate' && locales.includes(link.hreflang))
      })
      .map((link) => {
        return {
          ...link,
          href: !locales.includes('en') && link.id === 'i18n-xd'
            ? `${publicRuntimeConfig.baseUrl}${localePath({ name: getRouteBaseName() })}`
            : link.href,
        }
      }),
  ],
  meta: [
    ...head.value.meta.filter((meta) => {
      return meta.property === 'og:url'
        || meta.property === 'og:locale'
        || (meta.property === 'og:locale:alternate' && locales.includes(meta.content))
    }),
  ],
  script: [
    ...data.value.page.structuredData.map(({ contentData }) => ({
      innerHTML: JSON.stringify({
        ...contentData,
      }),
      type: 'application/ld+json',
    })),
    {
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': data.value.home.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('index')}`,
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': data.value.hub.trackingBreadcrumbTitle || 'Blog',
            'item': `${publicRuntimeConfig.baseUrl}${localePath('blog')}`,
          },
          {
            '@type': 'ListItem',
            'position': 3,
            'name': data.value.page.trackingBreadcrumbTitle || data.value.page.title,
          },
        ],
      }),
      type: 'application/ld+json',
    },
  ],
})

useSeoMeta({
  title: data.value.page.title,
  description: data.value.page.subtitle,
  ogTitle: data.value.page.title,
  ogDescription: data.value.page.subtitle,
  twitterTitle: data.value.page.title,
  twitterDescription: data.value.page.subtitle,
})

// Refs
const activeH2Index = ref(0)
const count = ref(0)
const h2InContent = ref([])
const isStuck = ref(false)
const mutationObserver = ref(null)
const readingLevelInPercentage = ref(0)
const requestAnimationFrameId = ref(null)
const timeoutId = ref(null)

// Computed Properties
const bottomContentOffer = computed(() => {
  return (
    data.value.page.bottomContentOffer
    && data.value.page.cluster
    && data.value.page.cluster.bottomContentCardCtaTitle
    && data.value.page.cluster.bottomContentCardText
    && data.value.page.cluster.bottomContentCardTitle
  )
})

const companies = computed(() => {
  return data.value.componentCustomersReviewLogo.customers
    .slice(0, 5)
    .map(customer => ({
      alt: `${customer.name} logo`,
      height: 48,
      params: {
        h: 48,
        w: 'auto',
      },
      url: customer.logoColor.url,
      width: Math.round(
        48 * (customer.logoColor.width / customer.logoColor.height),
      ),
    }))
})

const content = computed(() => {
  let indexOfFirstH2 = null
  let indexOfThirdtH2 = null
  let thirdH2 = null
  let countOfH2 = 0

  return (data.value.page?.content || [])
    .map((item, index) => {
      if (item.__typename === 'CalltoactionRecord') {
        return {
          ...item,
          patternDesktop: {
            ...data.value.componentPromotion.patternDesktop,
          },
          patternMobile: {
            ...data.value.componentPromotion.patternMobile,
          },
        }
      }
      else if (item.__typename === 'ContentCustomerStoryAndQuoteRecord') {
        return {
          ...item,
          cta: {
            slot: data.value.componentGlobal.readTheCustomerStory,
            to: localePath({
              name: 'customers-slug',
              params: {
                slug: item.customer.slug,
              },
            }),
          },
        }
      }
      else if (item.__typename === 'MoleculeSmallCallToActionRecord') {
        if (
          item.callToAction.slug
          === `https://${publicRuntimeConfig.appBaseUrl}/#/signup`
          && isLogged.value
        ) {
          item.callToAction = {
            ...item.callToAction,
            slug: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
            slot: data.value.componentGlobal.ctaUpgradeNow,
          }
        }
        return {
          ...item,
          patternDesktop: {
            ...data.value.componentPromotion.patternDesktop,
          },
          patternMobile: {
            ...data.value.componentPromotion.patternMobile,
          },
        }
      }
      else if (item.__typename === 'TextRecord') {
        const text = $md.render(item.text)
        const hasH2 = text.match(/<h2>(.*?)<\/h2>/g)
        if (hasH2) {
          for (let i = 0; i < hasH2.length; i++) {
            if (countOfH2 < 3) countOfH2 += 1
            if (countOfH2 === 3) {
              thirdH2 = hasH2[i]
              break
            }
          }

          if (displayTextBasedInlineCta.value) {
            if (indexOfFirstH2 === null) {
              indexOfFirstH2 = index
              const h2 = text.match(/<h2>(.*?)<\/h2>/g)[0]
              const split = text.split(h2)
              const newElements = []

              if (split[0] !== '') {
                newElements.push({
                  id: item.id + Math.random(0, 1) * 100,
                  __typename: item.__typename,
                  text: split[0],
                })
              }

              newElements.push({
                id: item.id + Math.random(0, 1) * 100,
                __typename: 'inlineTextCtaRecord',
                event: {
                  name: 'Cluster CTA clicked',
                  attributes: {
                    cluster: data.value.page.cluster?.title,
                    label: 'Inline text CTA',
                  },
                },
                to: ctaLink.value,
                text:
                  data.value.page.cluster
                  && data.value.page.cluster.textBasedInlineCtaText,
              })

              if (contentCardCTA.value) {
                newElements.push({
                  id: item.id + Math.random(0, 1) * 100,
                  ...contentCardCTA.value,
                  event: {
                    name: 'Cluster CTA clicked',
                    attributes: {
                      ...contentCardCTA.value.event.attributes,
                      label: 'Inline content card primary',
                    },
                  },
                })
              }

              newElements.push({
                id: item.id + Math.random(0, 1) * 100,
                __typename: item.__typename,
                text: `${h2}${split[1]}`,
              })

              return newElements
            }
          }

          if (displayInlineContentCard.value) {
            if (indexOfThirdtH2 === null && thirdH2) {
              const h2 = thirdH2.match(/<h2>(.*?)<\/h2>/g)[0]
              if (thirdH2 && countOfH2 === 3) {
                indexOfThirdtH2 = index
                const split = text.split(h2)
                const beforeH2 = {
                  id: item.id + Math.random(0, 1) * 100,
                  __typename: item.__typename,
                  text: split[0],
                }
                const remainingContent = {
                  id: item.id + Math.random(0, 1) * 100,
                  __typename: item.__typename,
                  text: `${h2}${split[1]}`,
                }
                const contentCardCTANode = {
                  id: item.id + Math.random(0, 1) * 100,
                  ...contentCardCTA.value,
                }
                let newElements = []
                if (split[0] === '')
                  newElements = [contentCardCTANode, remainingContent]
                else
                  newElements = [
                    beforeH2,
                    contentCardCTANode,
                    remainingContent,
                  ]
                return newElements
              }
            }
          }
        }
        return {
          ...item,
          text,
        }
      }
      else if (item.__typename === 'MoleculeDefinitionRecord') {
        return {
          ...item,
          content: $md.render(item.content),
        }
      }
      return item
    })
    .flat()
})

const contentCardCTA = computed(() => {
  if (Object.keys(inlineContentCard.value).length === 0) {
    return null
  }

  return {
    __typename: 'inlineContentCardRecord',
    ...inlineContentCard.value,
  }
})

const ctaLink = computed(() => {
  if (!data.value.page.cluster) return ''
  return data.value.page.cluster.relatedContentOffer
    ? isURL(data.value.page.cluster.relatedContentOffer.slug)
      ? data.value.page.cluster.relatedContentOffer.slug
      : data.value.page.cluster.relatedContentOffer.__typename
        === 'ModelOfferRecord'
        ? '#form-ebook'
        : localePath({
            name: 'resources-type-slug',
            params: {
              slug: data.value.page.cluster.relatedContentOffer.slug,
              type: $slugify(data.value.componentResourceType.name),
            },
          })
    : data.value.page.cluster.urlForTheCta.includes(
      `${publicRuntimeConfig.appBaseUrl}/#/signup`,
    )
      ? primaryButton.value.to
      : data.value.page.cluster.urlForTheCta
})

const displayInlineContentCard = computed(() => {
  return (
    data.value.page.inlineContentCard
    && Object.keys(inlineContentCard.value).length > 0
  )
})

const displaySidebarContentCard = computed(() => {
  return (
    data.value.page.sidebarContentCard
    && Object.keys(sidebarContentCardData.value).length > 0
  )
})

const displayRelatedPillarPage = computed(() => {
  return (
    data.value.page.relatedPillarPage
    && Object.keys(relatedPillarPageContent.value).length > 0
  )
})

const displayTextBasedInlineCta = computed(() => {
  return (
    data.value.page.inlineTextCta && Object.keys(ctaLink.value).length > 0
  )
})

const headerCallToAction = computed(() => {
  return {
    event: {
      name: 'Cluster CTA clicked',
      attributes: {
        cluster: data.value.page.cluster?.title,
        label: 'Header section CTA',
      },
    },
    hasIcon: !data.value.page.cluster?.urlForTheCta.includes('app.livestorm.co/#/signup'),
    slot: data.value.page.cluster?.urlForTheCta.includes('app.livestorm.co/#/signup')
      ? primaryButton.value.slot
      : data.value.page.cluster.headerCtaCtaTitle,
    to: data.value.page.cluster?.urlForTheCta.includes('app.livestorm.co/#/signup')
      ? primaryButton.value.to
      : ctaLink.value,
  }
})

const headerCallToActionExists = computed(() => {
  return (
    displayInlineContentCard.value
    && data.value.page.headerCta
    && data.value.page.cluster
    && data.value.page.cluster.headerCtaCtaTitle
    && data.value.page.cluster.headerCtaText
  )
})

const heroImage = computed(() => {
  return data.value.page?.coverWithImgix || data.value.page?.coverImage || null
})

const informations = computed(() => {
  return `${publishedOn.value} ${updatedAt.value} ${timeReading.value}`
})

const inlineContentCard = computed(() => {
  if (!data.value.page.cluster) {
    return {}
  }

  if (data.value.page.cluster.relatedContentOffer) {
    if (
      data.value.page.cluster.relatedContentOffer.__typename
      === 'ModelOfferRecord'
    ) {
      return {
        backgroundImage: data.value.page.cluster.relatedContentOffer.backgroundImage,
        callToAction: data.value.page.cluster.relatedContentOffer.resourceType.cta,
        coMarketing: data.value.page.cluster.relatedContentOffer.coMarketing,
        description:
          data.value.page.cluster.inlineContentCardText
          || data.value.page.cluster.relatedContentOffer.shortDescription,
        cluster: data.value.page.cluster.title,
        event: {
          name: 'Cluster CTA clicked',
          attributes: {
            cluster: data.value.page.cluster?.title,
            label: 'Inline content card',
          },
        },
        image: data.value.page.cluster.relatedContentOffer.image,
        tagColor: data.value.page.cluster.relatedContentOffer.resourceType.color,
        tagIcon: data.value.page.cluster.relatedContentOffer.resourceType.icon,
        title: data.value.page.cluster.relatedContentOffer.title,
        to: isURL(data.value.page.cluster.relatedContentOffer.slug)
          ? data.value.page.cluster.relatedContentOffer.slug
          : '#form-ebook',
        trackName:
          data.value.page.cluster.relatedContentOffer.trackName
          || data.value.page.cluster.relatedContentOffer.trackNameFallback,
        type: data.value.page.cluster.relatedContentOffer.resourceType.name,
        url: localePath({
          name: 'file-slug',
          params: {
            slug: data.value.page.cluster.relatedContentOffer.ebook?.slug,
          },
        }),
      }
    }
    else {
      const type = data.value.allResourceTypes.find(
        type => type.name === 'Quiz',
      )

      return {
        backgroundImage:
          data.value.page.cluster.inlineContentCardImage
          || data.value.page.cluster.relatedContentOffer.cardBackgroundImage,
        callToAction:
          data.value.page.cluster?.urlForTheCta.includes('app.livestorm.co/#/signup')
            ? primaryButton.value.slot
            : data.value.page.cluster.inlineContentCardCtaTitle || data.value.hub.readMore,
        description:
          data.value.page.cluster.inlineContentCardText
          || data.value.page.cluster.relatedContentOffer.cardShortDescription,
        event: {
          name: 'Cluster CTA clicked',
          attributes: {
            cluster: data.value.page.cluster?.title,
            label: 'Inline content card',
          },
        },
        tagColor: type.color,
        tagIcon: type.icon,
        title:
          data.value.page.cluster.inlineContentCardTitle
          || data.value.page.cluster.relatedContentOffer.cardShortTitle,
        to: data.value.page.cluster.inlineContentCardCtaLink
          ? data.value.page.cluster.inlineContentCardCtaLink
          : localePath({
              name: 'resources-quiz-slug',
              params: {
                slug: data.value.page.cluster.relatedContentOffer.slug,
              },
            }),
        topic:
          data.value.page.cluster.relatedContentOffer.cardResourceTopic.name,
        type:
          data.value.page.cluster.inlineContentCardTag
          || data.value.componentResourceType.name,
      }
    }
  }
  else {
    if (!data.value.page.cluster.inlineContentCardTitle) {
      return {}
    }

    return {
      backgroundImage: data.value.page.cluster.inlineContentCardImage,
      callToAction: data.value.page.cluster.urlForTheCta.includes('app.livestorm.co/#/signup')
        ? primaryButton.value.slot
        : data.value.page.cluster.inlineContentCardCtaTitle,
      description: data.value.page.cluster.inlineContentCardText,
      event: {
        name: 'Cluster CTA clicked',
        attributes: {
          cluster: data.value.page.cluster?.title,
          label: 'Inline content card',
        },
      },
      tagColor: 'pale-blue',
      tagIcon: {
        name: 'target',
      },
      title: data.value.page.cluster.inlineContentCardTitle,
      to: data.value.page.cluster.inlineContentCardCtaLink
        ? data.value.page.cluster.inlineContentCardCtaLink
        : data.value.page.cluster?.urlForTheCta.includes('app.livestorm.co/#/signup')
          ? primaryButton.value.to
          : data.value.page.cluster.urlForTheCta,
      type: data.value.page.cluster.inlineContentCardTag,
    }
  }
})

const primaryButton = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: data.value.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: data.value.componentGlobal.ctaGetStartedForFree,
      }
})

const publishedOn = computed(() => {
  const { publishedOn } = data.value.hub
  const date = formatDate(data.value.page.date)
  return `${publishedOn} <span class="hightlighted">${date}</span>`
})

const relatedPillarPageContent = computed(() => {
  if (!data.value.page.cluster || !data.value.page.cluster.relatedPillarPage)
    return {}
  return {
    backgroundImage:
      data.value.page.cluster.relatedPillarPage.backgroundImage,
    callToAction: data.value.hub.readMore,
    cluster: data.value.page.cluster.title,
    description: data.value.page.cluster.relatedPillarPage.shortDescription,
    event: {
      name: 'Related pillar page clicked',
      attributes: {
        cluster: data.value.page.cluster?.title,
        label: 'Related pillar page',
      },
    },
    tagColor: 'pale-blue',
    tagIcon: {
      name: 'target',
    },
    title: data.value.page.cluster.relatedPillarPage.shortTitle,
    to: localePath({
      name: 'resources-guides-slug',
      params: {
        slug: data.value.page.cluster.relatedPillarPage.slug,
      },
    }),
    type: 'Guide',
  }
})

const timeReading = computed(() => {
  let allContent = ''
  const wordsPerMinute = 1500
  data.value.page.content
  && data.value.page.content.forEach((item) => {
    if (item.__typename === 'TextRecord') {
      allContent += item.text
    }
  })

  const minutes = Math.max(Math.floor(allContent.length / wordsPerMinute), 1)
  return ` • ${data.value.hub.about} <span class="hightlighted">${minutes} ${data.value.componentGlobal.minRead}</span>`
})

const trackingPlanOfTheBottomContentCard = computed(() => {
  return {
    name: 'Cluster CTA clicked',
    attributes: {
      cluster: data.value.page.cluster?.title,
      label: 'Bottom content card',
    },
  }
})

const sidebarContentCardData = computed(() => {
  if (!data.value.page.cluster || !data.value.page.cluster.relatedContentOffer) {
    return {}
  }

  if (data.value.page.cluster.relatedContentOffer) {
    if (
      data.value.page.cluster.relatedContentOffer.__typename
      === 'ModelOfferRecord'
    ) {
      return {
        backgroundImage:
          data.value.page.cluster.relatedContentOffer.backgroundImage,
        callToAction:
          data.value.page.cluster.relatedContentOffer.resourceType.cta,
        cluster: data.value.page.cluster.title,
        event: {
          name: 'Cluster CTA clicked',
          attributes: {
            cluster: data.value.page.cluster?.title,
            label: 'Sidebar content card',
          },
        },
        image: data.value.page.cluster.relatedContentOffer.image,
        tagColor:
          data.value.page.cluster.relatedContentOffer.resourceType.color,
        tagIcon:
          data.value.page.cluster.relatedContentOffer.resourceType.icon,
        title: data.value.page.cluster.relatedContentOffer.title,
        to: isURL(data.value.page.cluster.relatedContentOffer.slug)
          ? data.value.page.cluster.relatedContentOffer.slug
          : '#form-ebook',
        type: data.value.page.cluster.relatedContentOffer.resourceType.name,
      }
    }
    else {
      const type = data.value.allResourceTypes.find(
        type => type.name === 'Quiz',
      )

      return {
        backgroundImage:
          data.value.page.cluster.relatedContentOffer.cardBackgroundImage,
        callToAction: data.value.hub.readMore,
        description:
          data.value.page.cluster.relatedContentOffer.cardShortDescription,
        event: {
          name: 'Cluster CTA clicked',
          attributes: {
            cluster: data.value.page.cluster?.title,
            label: 'Sidebar content card',
          },
        },
        tagColor: type.color,
        tagIcon: type.icon,
        title: data.value.page.cluster.relatedContentOffer.cardShortTitle,
        to: localePath({
          name: 'resources-quiz-slug',
          params: {
            slug: data.value.page.cluster.relatedContentOffer.slug,
          },
        }),
        topic:
          data.value.page.cluster.relatedContentOffer.cardResourceTopic.name,
        type: data.value.componentResourceType.name,
      }
    }
  }
  return {
    backgroundImage: data.value.page.cluster.pushCover,
    callToAction: data.value.page.cluster?.urlForTheCta.includes('app.livestorm.co/#/signup')
      ? primaryButton.value.slot
      : data.value.page.cluster.pushCtaTitle,
    event: {
      name: 'Cluster CTA clicked',
      attributes: {
        cluster: data.value.page.cluster?.title,
        label: 'Sidebar content card',
      },
    },
    tagColor: 'livestorm-blue',
    title: data.value.page.cluster.pushTitle,
    to: data.value.page.cluster?.urlForTheCta.includes('app.livestorm.co/#/signup')
      ? primaryButton.value.to
      : data.value.page.cluster.pushCtaLink,
    type: data.value.page.cluster.pushTag
      ? data.value.page.cluster.pushTag
      : undefined,
  }
})

const updatedAt = computed(() => {
  if (!data.value.page._updatedAt) {
    return ''
  }

  if (dateDiff(data.value.page.date, data.value.page._updatedAt) <= 0) {
    return ''
  }

  const { updatedOn } = data.value.hub
  const date = formatDate(data.value.page._updatedAt)
  return ` • ${updatedOn} <span class="hightlighted">${date}</span>`
})

// Watchers
watch(() => count.value, () => {
  nextTick(() => {
    getH2InContent()
  })
})

// Methods
function dateDiff(date1, date2, unit = 'milliseconds') {
  const d1 = new Date(date1)
  const d2 = new Date(date2)
  if (isNaN(d1) || isNaN(d2)) return NaN

  const diffMs = d2 - d1

  const unitConversion = {
    milliseconds: 1,
    seconds: 1000,
    minutes: 1000 * 60,
    hours: 1000 * 60 * 60,
    days: 1000 * 60 * 60 * 24,
    weeks: 1000 * 60 * 60 * 24 * 7,
    months: 1000 * 60 * 60 * 24 * 30.436875, // Approximate
    years: 1000 * 60 * 60 * 24 * 365.25, // Considering leap years
  }

  return diffMs / (unitConversion[unit] || 1)
}

function formatDate(date) {
  const locales = {
    en: 'en-US',
    fr: 'fr-FR',
    es: 'es-ES',
    de: 'de-DE',
  }

  const selectedLocale = locales[locale.value] || locales.en

  return new Intl.DateTimeFormat(selectedLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date))
}

function getH2InContent() {
  h2InContent.value = [
    ...document.querySelector('.page .container.content').querySelectorAll('h2'),
  ]
}

function getOffsetTop($el) {
  let curtop = 0
  if ($el.offsetParent) {
    do {
      curtop += $el.offsetTop
    } while (($el = $el.offsetParent))
    return curtop
  }
}

function goToTitle(index) {
  activeH2Index.value = index
  const { offsetHeight } = document.querySelector('.app__layout-header')
  const title = [
    ...document.querySelector('.page .container.content').querySelectorAll('h2'),
  ].find((_, i) => i === index)
  const top = getOffsetTop(title) - offsetHeight
  window.scrollTo({
    top,
    behavior: 'smooth',
  })
}

function isSticky(element) {
  if ('sticky' !== getComputedStyle(element).position) {
    return false
  }

  return (
    1
    >= element.getBoundingClientRect().top
    - parseInt(getComputedStyle(element).top)
  )
}

function isURL(to) {
  const pattern
    = /[-a-zA-Z0-9@:%_+.~#?&//=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&//=]*)?/gi
  return pattern.test(to)
}

function onRequestAnimationFrame() {
  const { clientHeight } = document.querySelector('.page .container.content')
  const { scrollY } = window
  readingLevelInPercentage.value = Math.min(
    (scrollY * 100) / clientHeight,
    100,
  )
  requestAnimationFrameId.value = window.requestAnimationFrame(
    onRequestAnimationFrame,
  )
  const activeH2 = h2InContent.value
    .map(({ offsetHeight, offsetTop }, index) => ({
      index,
      offsetHeight,
      offsetTop,
    }))
    .reverse()
    .find(
      ({ offsetHeight, offsetTop }) =>
        scrollY >= offsetTop - offsetHeight * 2,
    )
  if (activeH2) activeH2Index.value = activeH2.index
}

function onScroll() {
  clearTimeout(timeoutId.value)
  timeoutId.value = setTimeout(() => {
    toggleSticky(document.querySelector('.page .table-content-container'))
  }, 10)
}

function returnUniqueObjectFromArray(arr, key) {
  return [...new Map(arr.map(item => [item[key], item])).values()]
}

function subscribeToTheNewsletter(email) {
  if (window.Didomi) window.Didomi.setUserAgreeToAll()
  track('Blog list joined', {
    category: 'Form',
    locale: locale.value,
    title: data.value.page.title,
    email,
  })
}

function toggleSticky(element) {
  isStuck.value = isSticky(element)
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)

  if (requestAnimationFrameId.value) {
    window.cancelAnimationFrame(requestAnimationFrameId.value)
  }

  if (mutationObserver.value) {
    mutationObserver.value.disconnect()
    mutationObserver.value = null
  }
})

onMounted(() => {
  window.addEventListener('scroll', onScroll)

  requestAnimationFrameId.value = window.requestAnimationFrame(
    onRequestAnimationFrame,
  )
  page(data.value.page?.trackName || data.value.page?.title)
  setAvailableLanguages(data.value.page.alternativeVersions.map(({ locale }) => locale))
  updateListItems([
    {
      item: localePath('index'),
      name: data.value.home.trackingBreadcrumbTitle,
    },
    {
      item: localePath('blog'),
      name: data.value.hub.trackingBreadcrumbTitle || 'Blog',
    },
    {
      item: localePath({ name: getRouteBaseName() }),
      name: data.value.page.trackingBreadcrumbTitle || data.value.page.title,
    },
  ])

  nextTick(() => {
    getH2InContent()

    if (inlineContentCard.value.url) {
      setResource({
        ...inlineContentCard.value,
        event: {
          name: 'Offer clicked',
          attributes: {
            label: 'Thank you pop up CTA',
          },
        },
        title: data.value.page.cluster.relatedContentOffer.title || inlineContentCard.value.title,
        to: inlineContentCard.value.url,
      })
    }

    mutationObserver.value = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          count.value++
        }
      })
    })
    mutationObserver.value.observe(document.querySelector('.page'), {
      attributes: true,
      childList: false,
      characterData: false,
    })

    toggleSticky(document.querySelector('.page .table-content-container'))
  })
})
</script>

<style lang="postcss" scoped>
.page {
  @apply flex-col items-center;

  &.experience {
    .content {
      .primary-content {
        .rich-text {
          :deep(.inline-text-line-record) {
            @apply hidden;
          }

          :deep(.inline-text-line-record) + .inline-content-card-record {
            @apply flex;
          }
        }

        .footer {
          .small-call-to-action {
            @apply hidden;
          }

          .content-card {
            @apply flex;
          }
        }
      }
    }
  }

  .header {
    @apply py-20 flex flex-col items-center bg-blizzard-800;

    @screen xl {
      @apply py-14;
    }

    .left {
      @apply flex-col;

      @screen xl {
        width: 620px;
      }

      .button {
        @apply px-0 self-start;

        .tag {
          @apply bg-blizzard-100 text-blizzard-800;
        }
      }

      h1 {
        @apply mt-4 text-3xl leading-10 text-white;

        @screen mdminus {
          @apply font-medium text-3xl leading-10;
        }

        @screen mdplus {
          @apply font-normal text-5xl leading-16;
        }
      }

      p {
        @apply mt-4 text-white;
      }

      .informations {
        @apply mt-8 text-sm leading-6 text-blizzard-200;

        :deep(span) {
          @apply font-medium;
        }
      }

      .author {
        @apply mt-2 flex flex-row items-center;

        picture {
          @apply w-6 h-6 rounded-full;
        }

        p {
          @apply mt-0 ml-2 flex whitespace-normal text-sm leading-6 text-blizzard-200;
        }
      }

      .header-cta {
        @apply mt-8 py-4 px-6 w-full flex-col justify-between bg-winter-green-100;
        border-radius: 20px;

        @screen md {
          @apply py-6 flex-row items-center;
        }

        p {
          @apply mt-0 text-sm leading-6 text-winter-green-900 text-center;

          @screen md {
            @apply text-left;
          }
        }

        .button {
          @apply mt-4 px-4 self-center;

          @screen md {
            @apply mt-0 ml-4 w-auto flex-shrink-0;
          }

          i {
            @apply mr-2 text-white;
          }
        }
      }
    }

    .right {
      @apply hidden;

      @screen xl {
        @apply ml-10 flex;
        width: 620px;
      }
    }
  }

  .indicator {
    @apply sticky self-start bg-blizzard-500 z-30;
    border-radius: 0 2px 2px 0;
    top: 56px;
    height: 5px;
    width: 200px;

    @screen xl {
      top: 72px;
    }
  }

  .content {
    @apply mt-14 flex-col;

    @screen md {
      @apply mt-26;
    }

    @screen xl {
      @apply mt-14 flex-row;
    }

    .socials-links {
      @apply mt-10 flex flex-row order-3 gap-4 justify-center;

      @screen xl {
        @apply top-[129px] h-fit w-[70px] mt-0 sticky flex-col order-1 gap-6 justify-start;
      }

      .icon {
        @apply text-grey-blue-600 transition-colors duration-500 ease-in-out;

        &:hover {
          @apply text-grey-blue-800;
        }
      }
    }

    .table-content-container {
      @apply static top-13 flex-col order-1 h-fit py-4 bg-white border-b border-grey-blue-200 z-10;

      @screen xl {
        @apply sticky p-0 order-3 border-none;
        margin-left: 150px;
        top: 129px;
        width: 290px !important;
      }

      .content-card {
        @apply hidden;

        @screen xl {
          @apply mt-10 h-auto flex flex-col;
        }

        :deep(.head) {
          @screen xl {
            @apply h-[202px];
          }
        }

        :deep(.body) {
          @screen xl {
            @apply px-6;
          }

          .button {
            @apply text-left;
          }
        }
      }
    }

    .primary-content {
      @apply mt-10 flex-col order-2;

      @screen xl {
        @apply mt-0 ml-10;
        width: 730px;
      }

      :deep(.rich-text) {
        @apply flex-col px-0;

        .inline-text-line-record + .inline-content-card-record {
          @apply hidden;
        }

        .row {
          @apply mt-10;

          &:first-child {
            @apply mt-0;
          }
        }

        .row-image {
          @apply mx-auto;
          max-width: 840px;
        }

        .definition-record {
          @screen xl {
            @apply px-0 !important;
          }
        }

        ol {
          @apply pl-4;

          @screen md {
            @apply pl-8;
          }

          li {
            @apply font-normal text-origin leading-7 list-decimal list-inside;

            &::marker {
              @apply text-lg leading-7 font-medium text-blizzard-600;
            }
          }
        }

        ul {
          @apply pl-4;

          @screen md {
            @apply pl-8;
          }

          li {
            @apply mt-3;

            &:before {
              @apply bg-blizzard-600;
            }
          }
        }

        .call-to-action-record {
          @apply px-0;
        }

        p,
        li {
          @apply text-grey-blue-600;
        }

        p + h2,
        p + h3 {
          @apply mt-8;

          @screen md {
            @apply mt-10;
          }
        }

        p + h4,
        p + h5,
        p + h6 {
          @apply mt-8;

          @screen mdminus {
            @apply mt-6;
          }

          @screen mdplus {
            @apply mt-8;
          }
        }

        h2 + p {
          @apply mt-4;
        }

        h3 + p,
        h4 + p,
        h5 + p,
        h6 + p {
          @apply mt-3;
        }

        h2 {
          b,
          strong {
            @apply font-normal text-spring-green-600;
          }
        }

        h3 {
          b,
          strong {
            @apply font-medium text-winter-green-600;
          }
        }

        h4 {
          b,
          strong {
            @apply font-medium text-sunburst-red-400;
          }
        }

        h5 {
          b,
          strong {
            @apply font-medium;
          }
        }

        h6 {
          b,
          strong {
            @apply font-medium;
          }
        }
      }

      .footer {
        @apply flex-col mt-18;

        .small-call-to-action {
          @apply p-10;

          @screen md {
            @apply flex-col;
          }

          :deep(.left) {
            @apply text-center max-w-full;
          }

          :deep(.right) {
            @apply justify-center;

            @screen md {
              @apply mt-8;
            }

            .button {
              @apply bg-livestorm-blue-700;

              &:hover {
                @apply bg-livestorm-blue-800;
              }
            }
          }
        }

        .content-card {
          @apply hidden;
        }
      }
    }
  }

  .reviews {
    @apply justify-center mt-20 py-26 bg-livestorm-blue-100;

    @screen md {
      @apply mt-34;
    }

    :deep(.companies-trust) {
      .rating {
        @apply mt-0;
      }
    }
  }

  .reviews + .related-posts {
    @apply mt-0;
  }

  .container.author {
    @apply mt-26;

    .blog-author.in-content {
      @apply pb-0;
    }
  }

  .related-pillar {
    @apply mt-20 flex-col;

    @screen md {
      @apply mt-34;
    }

    .content-card {
      @apply mt-8 shadow-none border-2 border-grey-blue-200;

      :deep(.button) {
        @apply font-bold;
      }
    }
  }

  .related-posts {
    @apply mt-14 py-24 justify-center bg-grey-blue-100;

    @screen md {
      @apply mt-26;
    }

    .container {
      @apply flex-col;

      .wrapper {
        @apply flex-col;
        @screen md {
          @apply flex-row flex-wrap;
        }

        .blog-post {
          @apply mt-12 text-center;

          @screen md {
            @apply mt-16;
            width: calc(50% - 20px);
          }

          @screen xl {
            @apply ml-10 w-100;
          }

          :deep(.tag) {
            @apply mx-auto;
          }

          &:first-child {
            @apply mt-10;
          }

          &:nth-child(2) {
            @screen md {
              @apply mt-10;
            }
          }

          &:nth-child(3) {
            @screen xl {
              @apply mt-10;
            }
          }

          &:nth-child(2n) {
            @screen md {
              @apply ml-10;
            }
          }

          &:nth-child(3n + 1) {
            @screen xl {
              @apply ml-0;
            }
          }
        }
      }
    }
  }

  .container.newsletter {
    @apply mt-20;

    @apply mt-34;
  }
}
</style>
